import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"
import { Portuguese } from "flatpickr/dist/l10n/pt.js"

function currentFlatpickrLocale() {
  const locale = window.I18n?.locale || ""
  return locale.startsWith("pt") ? Portuguese : "default"
}

function pad(number) {
  return String(number).padStart(2, "0")
}

export default class extends Controller {
  static targets = ["trigger", "label", "panel", "input", "flatpickr", "flatpickrMount", "time"]
  static values = { minDate: String, placeholder: String }

  connect() {
    const current = this.parse(this.inputTarget.value)
    this.fp = flatpickr(this.flatpickrTarget, {
      inline: true,
      locale: currentFlatpickrLocale(),
      appendTo: this.flatpickrMountTarget,
      minDate: this.minDateValue || undefined,
      defaultDate: current || undefined
    })
    if (!this.timeTarget.value) this.timeTarget.value = this.defaultTime()

    this.outsideClick = (event) => {
      if (!this.element.contains(event.target)) this.close()
    }
    this.keydown = (event) => {
      if (event.key === "Escape" && this.isOpen) {
        event.preventDefault()
        this.close()
        this.triggerTarget.focus()
      }
    }
    document.addEventListener("click", this.outsideClick)
    document.addEventListener("keydown", this.keydown)
  }

  disconnect() {
    document.removeEventListener("click", this.outsideClick)
    document.removeEventListener("keydown", this.keydown)
    this.fp?.destroy()
  }

  get isOpen() {
    return !this.panelTarget.classList.contains("hidden")
  }

  toggle(event) {
    event.stopPropagation()
    this.isOpen ? this.close() : this.open()
  }

  open() {
    this.panelTarget.classList.remove("hidden")
    this.triggerTarget.setAttribute("aria-expanded", "true")
  }

  close() {
    this.panelTarget.classList.add("hidden")
    this.triggerTarget.setAttribute("aria-expanded", "false")
  }

  apply() {
    const [day] = this.fp.selectedDates
    if (!day) return

    const [hours, minutes] = (this.timeTarget.value || "00:00").split(":").map(Number)
    const date = new Date(day)
    date.setHours(hours, minutes, 0, 0)
    this.setDate(date)
    this.close()
    this.triggerTarget.focus()
  }

  clear() {
    this.fp.clear()
    this.write("", this.placeholderValue, true)
    this.close()
  }

  setDate(date) {
    this.fp.setDate(date, false)
    this.timeTarget.value = `${pad(date.getHours())}:${pad(date.getMinutes())}`
    const value = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${this.timeTarget.value}`
    const label = `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${this.timeTarget.value}`
    this.write(value, label, false)
  }

  write(value, label, empty) {
    this.inputTarget.value = value
    this.labelTarget.textContent = label
    this.labelTarget.classList.toggle("text-gray-500", empty)
    this.labelTarget.classList.toggle("dark:text-gray-400", empty)
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  parse(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value || "")
    if (!match) return null
    const [, year, month, day, hours, minutes] = match.map(Number)
    return new Date(year, month - 1, day, hours, minutes)
  }

  defaultTime() {
    const next = new Date(Date.now() + 3_600_000)
    return `${pad(next.getHours())}:00`
  }
}
